/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	DateHelper
} from '@shared/helpers/date.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	IKeyValuePair
} from '@shared/interfaces/application-objects/key-value-pair.interface';
import {
	IFileEntity
} from '@shared/interfaces/files/file-entity.interface';
import {
	DateTime
} from 'luxon';

/**
 * A class containining static methods for files.
 *
 * @export
*/
export class FileHelper
{
	/**
	 * The base entity type of a file.
	 *
	 * @static
	 * @type {string}
	 * @memberof FileHelper
	 */
	public static fileBaseType: string = AppConstants
		.fileBaseType;

	/**
	 * The base file category name
	 *
	 * @static
	 * @type {string}
	 * @memberof FileHelper
	 */
	public static fileBaseTypeCategory: string = AppConstants
		.fileBaseTypeCategory;

	/**
	 * the amount of bytes in a kilobyte
	 *
	 * @static
	 * @type {number}
	 * @memberof FileHelper
	 */
	public static readonly oneKilobyte: number = 1024;

	/**
	 * the amount of bytes in a megabyte
	 * @note 1024 * 1024
	 *
	 * @static
	 * @type {number}
	 * @memberof FileHelper
	 */
	public static readonly oneMegabyte: number = 1048576;

	/**
	 * the amount of bytes in a gigaabyte
	 *
	 * @static
	 * @type {number}
	 * @memberof FileHelper
	 */
	public static readonly oneGigabyte: number = 1073741824;

	/**
	 * A mapping/dictionary of mime type keywords to css icon classes.
	 *
	 * @type {Map}
	 * @memberof FileHelper
	 */
	private static readonly fileIcons: Map<string, string> =
		new Map<string, string>(
			[
				['default', 'fa-file-o'],
				['excel', 'fa-file-excel-o'],
				['pdf', 'fa-file-pdf-o'],
				['audio', 'fa-file-audio-o'],
				['word', 'fa-file-word-o' ],
				['archive', 'fa-file-archive-o'],
				['image', 'fa-file-image-o'],
				['text', 'fa-file-text-o'],
				['zip', 'fa-file-zip-o'],
				['powerpoint', 'fa-file-powerpoint-o'],
				['video', 'fa-file-video-o']
			]
		);

	/**
	 * Returns a friendly category name.
	 * @param {string} entityType
	 * The entity type name to convert.
	 *
	 * @returns {string}
	 * @memberof FileHelper
	 */
	public static getCategoryFromType(entityType: string): string
	{
		if (entityType
			.startsWith(this.fileBaseType) === false)
		{
			throw new Error(
				'Cannot get category for invalid file entity type '
					+ `"${entityType}"`);
		}

		if (entityType === this.fileBaseType)
		{
			return this.fileBaseTypeCategory;
		}

		return StringHelper.beforeCapitalSpaces(
			StringHelper.getLastSplitValue(
				entityType,
				'.'));
	}

	/**
	 * Gets an css class for an icon representing
	 * this file content type.
	 *
	 * @param {string} contentMimeType
	 *
	 * @returns {string}
	 * @memberof FileHelper
	 */
	public static getFileIconClass(contentMimeType: string): string
	{
		const mimeType: string = contentMimeType
			.toLowerCase();

		if (mimeType.includes('audio'))
		{
			return this.fileIcons.get('audio');
		}

		if (mimeType.includes('excel'))
		{
			return this.fileIcons.get('excel');
		}

		if (mimeType.includes('pdf'))
		{
			return this.fileIcons.get('pdf');
		}

		if (mimeType.includes('word'))
		{
			return this.fileIcons.get('word');
		}

		if (mimeType.includes('archive'))
		{
			return this.fileIcons.get('archive');
		}

		if (mimeType.includes('image'))
		{
			return this.fileIcons.get('image');
		}

		if (mimeType.includes('text'))
		{
			return this.fileIcons.get('text');
		}

		if (mimeType.includes('zip'))
		{
			return this.fileIcons.get('zip');
		}

		return this.fileIcons.get('default');
	}

	/**
	 * Gets the custom metadata in displayable key/value pairs.
	 *
	 * @param {IFileEntity} fileEntity
	 * The file entity with the custom metadata.
	 * @param {string[]} includeProperties
	 * The optional filter of properties to include.
	 * If this is null all properties will be included.
	 * @returns {IKeyValuePair[]}
	 * The filtered metadata as displayable key/value pairs
	 * @memberof FileHelper
	 */
	 public static getCustomMetadata(
		fileEntity: IFileEntity,
		includeProperties: string[]): IKeyValuePair[]
	{
		const custom: any
			 = fileEntity?.data?.metadata?.custom;

		if (AnyHelper.isNullOrEmpty(custom))
		{
			return [];
		}

		const entries: any = AnyHelper.isNullOrEmpty(includeProperties)
			? Object.entries(custom)
			: Object.entries(custom)
				.filter(([key, _]) =>
					includeProperties.includes(key));

		return entries.map(
			([key, value]) =>
				<IKeyValuePair>{
					key: StringHelper.toProperCase(
						StringHelper.beforeCapitalSpaces(key)),
					value: this.formatMetadataValue(value)
				});
	}

	/**
	 * Gets a metadata value.
	 *
	 * @param {any} value
	 * The file entity with the custom metadata.
	 * @returns {string}
	 * The filtered metadata as displayable key/value pairs
	 * @memberof FileHelper
	 */
	public static formatMetadataValue(
		value: any): string
	{
		if (AnyHelper.isNullOrWhitespace(value))
		{
			return AppConstants.empty;
		}

		const valueString: string = `${value}`;

		if (isNaN(value)
			&& DateHelper.isDate(valueString))
		{
			const localDate: Date = DateHelper
				.fromUtcSystemIsoToSimulatedLocalDate(valueString);

			const dateTime: DateTime = DateTime
				.fromJSDate(localDate);

			return DateHelper.formatDate(dateTime);
		}

		return valueString;
	}

	/**
	 * Determines if a file is available for download/open operations.
	 * A file is considered available if it has a non-null, non-whitespace
	 * storage location.
	 *
	 * @static
	 * @param {IFileEntity} fileEntity
	 * The file entity to check.
	 * @returns {boolean}
	 * True if file has a valid storage location, false otherwise.
	 * @memberof FileHelper
	 */
	public static isFileAvailable(
		fileEntity: IFileEntity): boolean
	{
		return !AnyHelper.isNullOrWhitespace(
			fileEntity?.data?.storage?.location);
	}

	/**
	 * Gets the file extension safely, returning "unknown" for unavailable files.
	 *
	 * @static
	 * @param {IFileEntity} fileEntity
	 * The file entity to check.
	 * @returns {string}
	 * The file extension or "unknown" if file is unavailable.
	 * @memberof FileHelper
	 */
	public static getFileExtensionOrDefault(
		fileEntity: IFileEntity): string
	{
		if (!FileHelper.isFileAvailable(fileEntity))
		{
			return AppConstants.unknown;
		}

		return fileEntity?.data?.metadata?.extension ?? AppConstants.unknown;
	}

	/**
	 * Gets the MIME type safely, returning null for unavailable files.
	 *
	 * @static
	 * @param {IFileEntity} fileEntity
	 * The file entity to check.
	 * @returns {string | null}
	 * The MIME type or null if file is unavailable.
	 * @memberof FileHelper
	 */
	public static getFileMimeTypeOrDefault(
		fileEntity: IFileEntity): string | null
	{
		if (!FileHelper.isFileAvailable(fileEntity))
		{
			return null;
		}

		return fileEntity?.data?.metadata?.mimeType ?? null;
	}

	/**
	 * Formats byte size into human-readable format.
	 *
	 * @static
	 * @param {number} bytes
	 * The size in bytes.
	 * @returns {string}
	 * Formatted size string (e.g., "1.5 MB").
	 * @memberof FileHelper
	 */
	public static formatSize(bytes: number): string
	{
		if (AnyHelper.isNull(bytes) || bytes < 0)
		{
			return '0 Bytes';
		}

		const units:
			{
				divisor: number;
				label: string;
			}[] =
			[
				{
					divisor: FileHelper.oneGigabyte,
					label: 'GB'
				},
				{
					divisor: FileHelper.oneMegabyte,
					label: 'MB'
				},
				{
					divisor: FileHelper.oneKilobyte,
					label: 'KB'
				}
			];

		for (const unit of units)
		{
			if (bytes >= unit.divisor)
			{
				const size: number =
					Number.parseFloat(
						(bytes / unit.divisor)
							.toFixed(3));

				return `${size} ${unit.label}`;
			}
		}

		return `${bytes} Bytes`;
	}
}
